import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { fetchUserReservations } from "../api/auth";
import { useAuth } from "../hooks/useAuth";
import LoadingSpinner from "../components/LoadingSpinner";
import Footer from "../components/Footer";
import PageHero from "../components/PageHero";
import heroImage from "../assets/Sample/adrien-bruneau-8cpR_Yf0rQs-unsplash.jpg";
import searchIcon from "../assets/search-icon.svg";
import chevronRight from "../assets/chevron-right.svg";

const koulen = { fontFamily: "Koulen, sans-serif" };

const formatTime = (value) =>
    new Date(value).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });

const truncate = (text, max = 140) =>
    text && text.length > max ? `${text.substring(0, max)} [...]` : text || "";

// Decorative double line shown above each month heading (matches the
// dashboard panel hamburger, scaled up to the 86px design width).
function DoubleLine() {
    return (
        <div className="w-[72px]">
            <div className="border-t-[3px] border-black" />
            <div className="border-t-[3px] border-black mt-[6px]" />
        </div>
    );
}

function EventCard({ item }) {
    const detailPath = item.eventId ? `/event/detail/${item.eventId}` : null;
    const photo = item.photo;

    return (
        <div className="flex flex-col md:flex-row gap-6 md:gap-[48px]">
            {/* Photo block with the date overlay and edge lines */}
            <div className="relative w-full md:w-2/5 max-w-[573px] aspect-[573/494] rounded-[10px] bg-[#d9d9d9] overflow-hidden shrink-0">
                {photo && (
                    <img alt={item.title} src={photo} className="absolute inset-0 w-full h-full object-cover" />
                )}
                {/* Vertical lines near the right edge */}
                <div className="absolute top-0 bottom-0 right-[28px] w-[5px] bg-white" />
                <div className="absolute top-0 bottom-0 right-[48px] w-[5px] bg-white" />
                {/* Day number flanked by short vertical lines */}
                <div className="absolute top-[24px] left-[24px] flex items-center h-[84px]">
                    <div className="w-[5px] h-full bg-white" />
                    <span
                        className="px-[8px] text-center text-[80px] leading-none text-white"
                        style={koulen}
                    >
                        {item.day}
                    </span>
                    <div className="w-[5px] h-full bg-white" />
                </div>
            </div>

            {/* Text block */}
            <div className="relative flex-1 flex flex-col pt-[8px] pb-[60px] md:pb-[8px]">
                <h3 className="text-[28px] md:text-[37px] leading-[1.2] text-black tracking-[0.48px] uppercase" style={koulen}>
                    {item.title}
                </h3>
                <p className="text-[28px] md:text-[37px] leading-[1.2] text-black tracking-[0.48px]" style={koulen}>
                    {item.time}
                </p>
                <p className="mt-[16px] mb-[48px] md:mb-[56px] text-[20px] leading-[1.4] text-[#5e5e5e] max-w-[896px]">
                    {truncate(item.description)}
                </p>
                {detailPath && (
                    <Link
                        to={detailPath}
                        aria-label={`View ${item.title} details`}
                        className="absolute bottom-0 right-0"
                    >
                        <img alt="" src={chevronRight} className="w-[35px] h-[46px]" />
                    </Link>
                )}
            </div>
        </div>
    );
}

export default function Upcoming() {
    const { isAuthenticated, isLoadingUser } = useAuth();
    const [search, setSearch] = useState("");

    const { data: reservations = [], isLoading } = useQuery({
        queryKey: ["reservations"],
        queryFn: fetchUserReservations,
        enabled: isAuthenticated,
    });

    if (isLoadingUser || isLoading) return <LoadingSpinner />;

    const now = new Date();

    // Upcoming only. Past events live on the Service History page, which pairs
    // each one with its logged hours and links through to its detail page.
    const items = reservations
        .filter((res) => {
            const endDate = res.event?.end_date ? new Date(res.event.end_date) : null;
            return Boolean(endDate) && endDate >= now && res.status === "confirmed";
        })
        .map((res) => {
            const startDate = new Date(res.event.start_date);
            return {
                id: res.id,
                eventId: res.event.pk,
                title: res.event.title || "Event",
                description: res.event.description,
                photo: res.event.photos?.length > 0 ? res.event.photos[0].file : null,
                startDate,
                day: startDate.getDate(),
                time: `${formatTime(res.event.start_date)} - ${formatTime(res.event.end_date)}`,
                monthLabel: `${startDate
                    .toLocaleString("en-US", { month: "long" })
                    .toUpperCase()} ${startDate.getFullYear()}`,
            };
        })
        .filter((item) => item.title.toLowerCase().includes(search.trim().toLowerCase()))
        .sort((a, b) => a.startDate - b.startDate);

    // Group into ordered month sections
    const months = [];
    items.forEach((item) => {
        const last = months[months.length - 1];
        if (last && last.label === item.monthLabel) last.items.push(item);
        else months.push({ label: item.monthLabel, items: [item] });
    });

    return (
        <div className="bg-white text-left" style={{ fontFamily: "Arial, sans-serif" }}>
            {/* Hero banner */}
            <PageHero image={heroImage} title="Upcoming" />

            <main className="mt-0 px-6 sm:px-10 xl:px-[83px] pt-[35px] pb-[120px] max-w-[1728px] mx-auto">
                {/* Search bar */}
                <div className="flex justify-end">
                    <div className="flex items-end gap-[8px] w-full max-w-[320px] border-b-[2px] border-black pb-[5px]">
                        <input
                            type="search"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            aria-label="Search events"
                            className="flex-1 min-w-0 bg-transparent outline-none text-[18px] text-black"
                        />
                        <img alt="" src={searchIcon} className="w-[24px] h-[28px]" />
                    </div>
                </div>

                {months.length > 0 ? (
                    months.map((month, i) => (
                        <section key={month.label} className={i === 0 ? "" : "mt-[80px]"}>
                            <DoubleLine />
                            <h2
                                className="mt-[40px] text-[40px] md:text-[48px] leading-normal text-black tracking-[2.4px]"
                                style={koulen}
                            >
                                {month.label}
                            </h2>
                            <div className="mt-[40px] flex flex-col gap-[48px] md:gap-[64px]">
                                {month.items.map((item) => (
                                    <EventCard key={item.id} item={item} />
                                ))}
                            </div>
                        </section>
                    ))
                ) : (
                    <p className="mt-[80px] text-[24px] text-[#777f88]">
                        {search ? "No events match your search." : "No upcoming events."}
                    </p>
                )}
            </main>

            <Footer />
        </div>
    );
}
